import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaEdit,
  FaBuilding,
  FaEnvelope,
  FaPhone,
  FaGlobe,
  FaMapMarkerAlt,
  FaIndustry,
  FaUsers,
  FaChartLine,
  FaCreditCard,
  FaCalendarAlt,
  FaEye,
  FaTimesCircle,
} from "react-icons/fa";
import { fetchCompanyProfile } from "../../Redux/companySlice";
import { subscriptionService } from "../../marketplace/services/subscriptionService";

const CompanyProfile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { company, loading, error } = useSelector((state) => state.company);
  const [selectedImage, setSelectedImage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [billingLoading, setBillingLoading] = useState(true);
  const [billingError, setBillingError] = useState(null);
  const [cancellationScheduled, setCancellationScheduled] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    // Always fetch the latest profile data when component mounts
    const loadProfile = async () => {
      try {
        const response = await dispatch(fetchCompanyProfile()).unwrap();
        if (response.success && response.company) {
          // If there's a profile image, preload it
          if (response.company.profileImage?.url) {
            const img = new Image();
            img.src = response.company.profileImage.url;
          }
        }
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    loadProfile();
  }, [dispatch]);

  // Load billing/subscription when company is available
  useEffect(() => {
    if (!company) return;
    let cancelled = false;
    const loadSubscription = async () => {
      setBillingLoading(true);
      setBillingError(null);
      try {
        const res = await subscriptionService.getSubscription();
        if (!cancelled && res?.success && res?.data?.subscription) {
          setSubscription(res.data.subscription);
        }
      } catch (err) {
        if (!cancelled) {
          setBillingError(err.message || "Failed to load billing");
          setSubscription(null);
        }
      } finally {
        if (!cancelled) setBillingLoading(false);
      }
    };
    loadSubscription();
    return () => { cancelled = true; };
  }, [company]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString(undefined, { dateStyle: "medium" });
  };

  const formatPlanName = (plan) => {
    if (!plan) return "—";
    const map = { basic: "Basic", pro: "Pro", pro_plus: "Pro Plus", premium: "Premium" };
    return map[plan] || plan;
  };

  const handleCancelSubscription = async () => {
    if (cancelLoading || cancellationScheduled) return;
    setShowCancelDialog(false);
    setCancelLoading(true);
    try {
      await subscriptionService.cancelSubscription();
      setCancellationScheduled(true);
      const res = await subscriptionService.getSubscription({ cacheBust: true });
      if (res?.success && res?.data?.subscription) {
        setSubscription(res.data.subscription);
      }
    } catch (err) {
      setBillingError(err.message || "Failed to cancel subscription");
    } finally {
      setCancelLoading(false);
    }
  };

  const getInitial = (name) => {
    if (company?.profileImage?.url) {
      return (
        <img 
          src={company.profileImage.url} 
          alt="Profile" 
          className="w-full h-full object-cover rounded-full"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = '';
            e.target.parentElement.innerHTML = name ? name.charAt(0).toUpperCase() : "?";
          }}
        />
      );
    }
    return name ? name.charAt(0).toUpperCase() : "?";
  };

  const formatPhoneNumber = (phone) => {
    if (!phone) return "";
    const cleaned = phone.replace(/\D/g, "");
    return `+91${cleaned.slice(0, 2)} ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  };

  const handleImageClick = () => {
    if (company?.profileImage?.url) {
      setSelectedImage(company.profileImage.url);
    }
  };

  const handleCloseModal = (e) => {
    if (e.target === e.currentTarget) {
      setSelectedImage(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-red-600">
            Error loading profile: {error}
          </h2>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-700">
            No company data available
          </h2>
          <button
            onClick={() => navigate("/company/edit-profile")}
            className="mt-4 px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
          >
            Complete Company Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-6xl mx-auto px-4 py-6">
       
        {/* Header with company logo */}
        <div className="relative h-40 bg-gradient-to-r from-orange-400 to-orange-600 rounded-2xl">
          <div className="absolute -bottom-12 left-4 sm:left-6 md:left-8">
            <div 
              className="relative h-24 w-24 sm:h-28 sm:w-28 rounded-full border-4 border-white bg-white shadow-md flex items-center justify-center text-3xl sm:text-4xl font-bold text-orange-500 overflow-hidden cursor-pointer hover:opacity-90 transition-opacity"
              onClick={handleImageClick}
            >
              {getInitial(company?.companyName)}
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={handleCloseModal}
          >
            <div 
              className="relative rounded-xl max-w-2xl w-full aspect-[4/3] p-4"
              onClick={e => e.stopPropagation()}
            >
              <button 
                onClick={() => setSelectedImage(null)}
                className="absolute -top-4 -right-4 px-3 py-1 text-sm font-medium bg-gray-100 text-gray-700 rounded-md hover:bg-gray-300 transition-all z-10 shadow"
              >
                Close
              </button>
              <div className="w-full h-full flex items-center justify-center">
                <img
                  src={selectedImage}
                  alt="Profile"
                  className="w-full h-full object-cover rounded-lg"
                  onError={(e) => {
                    e.target.onerror = null;
                    setSelectedImage(null);
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Cancel subscription confirmation dialog */}
        {showCancelDialog && (
          <div
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
            onClick={() => !cancelLoading && setShowCancelDialog(false)}
          >
            <div
              className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 border border-gray-200"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-black mb-2">Cancel subscription</h3>
              <p className="text-gray-700 text-sm leading-relaxed mb-6">
                Cancel your subscription at the end of the current billing period? You will keep access until then.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowCancelDialog(false)}
                  disabled={cancelLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-black border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-60"
                >
                  Go back
                </button>
                <button
                  type="button"
                  onClick={() => handleCancelSubscription()}
                  disabled={cancelLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-500 rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {cancelLoading ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      Cancelling…
                    </span>
                  ) : (
                    "Yes, cancel"
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Profile content */}
        <div className="mt-16 bg-white rounded-2xl shadow-lg p-2 md:p-4 lg:p-6 space-y-8">
          {/* Header Info */}
          <div className="flex flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-gray-900">
                {company.companyName}
              </h1>
              <p className="text-gray-600 mt-1 text-xs md:text-sm">{company.firstName}</p>
            </div>
            <button
              onClick={() => navigate("/company/edit-profile")}
              className="flex items-center gap-2 px-2 md:px-4 py-1 md:py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              <FaEdit />
              Edit Profile
            </button>
          </div>

          {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
            <StatCard
              icon={<FaBuilding />}
              title="Company Stage"
              value={company.companyStage || "Not Specified"}
            />
            <StatCard
              icon={<FaBuilding />}
              title="Account Type"
              value={company.companyAccountType || "agencies"}
            />
            <StatCard
              icon={<FaUsers />}
              title="Company Size"
              value={company.companySize || "Not Specified"}
            />
            <StatCard
              icon={<FaChartLine />}
              title="Status"
              value={company.isVerified ? "Verified" : "Pending Verification"}
            />
          </div>

          {/* Company Information */}
          <section>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Company Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
              <InfoItem
                icon={<FaEnvelope />}
                label="Email Address"
                value={company.email}
              />
              <InfoItem
                icon={<FaPhone />}
                label="Phone Number"
                value={formatPhoneNumber(company.phone)}
              />
              <InfoItem
                icon={<FaGlobe />}
                label="Website"
                value={company.companyWebsite || "Not Specified"}
              />
              <InfoItem
                icon={<FaMapMarkerAlt />}
                label="Location"
                value={company.location || "Not Specified"}
              />
            </div>
          </section>

          {/* Business Details */}
          <section>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Business Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
              <InfoItem
                icon={<FaIndustry />}
                label="Industry"
                value={company.industryType || "Not Specified"}
              />
              <InfoItem
                icon={<FaBuilding />}
                label="Company Stage"
                value={company.companyStage || "Not Specified"}
              />
            </div>
          </section>

          {/* Billing & Subscription */}
          <section>
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Billing & Subscription
            </h2>
            {billingLoading ? (
              <div className="flex items-center gap-2 text-gray-500 py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-orange-500" />
                <span>Loading billing details…</span>
              </div>
            ) : billingError ? (
              <div className="bg-red-50 rounded-xl p-4 text-red-700">
                {billingError}
              </div>
            ) : subscription ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
                  <StatCard
                    icon={<FaCreditCard />}
                    title="Plan"
                    value={formatPlanName(subscription.planType)}
                  />
                  <StatCard
                    icon={<FaCalendarAlt />}
                    title="Billing cycle"
                    value={subscription.billingCycle ? subscription.billingCycle.charAt(0).toUpperCase() + subscription.billingCycle.slice(1) : "—"}
                  />
                  <StatCard
                    icon={<FaChartLine />}
                    title="Status"
                    value={subscription.isActive ? "Active" : (subscription.status || "—")}
                  />
                </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 md:gap-4">
                  <InfoItem
                    icon={<FaCalendarAlt />}
                    label="Next billing / access until"
                    value={formatDate(subscription.expiryDate)}
                  />
                  {typeof subscription.viewsUsed === "number" && (
                    <InfoItem
                      icon={<FaEye />}
                      label="Profile views"
                      value={`${subscription.viewsUsed ?? 0} / ${subscription.viewLimit ?? "—"} used`}
                    />
                  )}
                  {subscription.campaignLimit != null && (
                    <InfoItem
                      icon={<FaChartLine />}
                      label="Campaign limit"
                      value={subscription.campaignLimitType === "active"
                        ? `${subscription.campaignLimit} active campaigns`
                        : String(subscription.campaignLimit)}
                    />
                  )}
                </div>
                {subscription.planType !== "basic" && subscription.isActive && (
                  <div className="pt-2 border-t border-gray-100">
                    {cancellationScheduled ? (
                      <p className="text-amber-700 bg-amber-50 rounded-lg px-2 md:px-4 py-1 md:py-2 inline-flex items-center gap-2">
                        <FaTimesCircle />
                        Subscription will end at the close of the current billing period. Access until {formatDate(subscription.expiryDate)}.
                      </p>
                    ) : (
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 md:gap-4 p-2 md:p-4 rounded-xl bg-orange-50 text-orange-500">
                        <div>
                          <h3 className="text-sm md:text-lg font-semibold text-black">Cancel Subscription</h3>
                          <p className="text-xs md:text-sm text-black mt-0.5">We&apos;ll be sad to see you go.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowCancelDialog(true)}
                          disabled={cancelLoading}
                          className="shrink-0 px-2 md:px-5 py-1 md:py-2.5 text-xs md:text-sm font-medium text-white bg-orange-500 border border-orange-500 rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {cancelLoading ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="animate-spin rounded-full h-3 w-3 md:h-4 md:w-4 border-2 border-white border-t-transparent" />
                              Cancelling…
                            </span>
                          ) : (
                            "Cancel"
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-500">No subscription data available.</p>
            )}
          </section>

          {/* Description */}
          {company.description && (
            <section>
              <h2 className="text-xl font-semibold text-gray-800 mb-4">
                About Company
              </h2>
              <div className="bg-gray-50 rounded-xl p-2 md:p-4">
                <p className="text-gray-700 leading-relaxed">
                  {company.description}
                </p>
              </div>
            </section>
          )}
        </div>
      </div>
    
    </div>
  );
};

const StatCard = ({ icon, title, value }) => (
  <div className="bg-orange-50 rounded-xl p-4 flex items-center gap-3">
    <div className="p-2 bg-orange-100 rounded-lg text-orange-500 text-lg md:text-xl">
      {icon}
    </div>
    <div>
      <p className="text-xs md:text-sm text-gray-500">{title}</p>
      <p className="font-semibold text-gray-900 text-xs md:text-sm">{value}</p>
    </div>
  </div>
);

const InfoItem = ({ icon, label, value }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-orange-100 rounded-lg mt-1 text-orange-500 text-lg md:text-xl">
      {icon}
    </div>
    <div>
      <p className="text-xs md:text-sm text-gray-500">{label}</p>
      <p className="font-medium text-gray-900 text-xs md:text-sm">{value}</p>
    </div>
  </div>
);

export default CompanyProfile; 